import type { Card, CardMessage, ProviderId } from '../types';
import { normalizeLanguage } from '../languages';
import { newId } from '../util/id';

/** Keys written by the original 2025 Cue (localStorage only). */
const LEGACY_CHATS = 'cue-chats';
const LEGACY_SETTINGS = 'cue-settings';
const LEGACY_FLAG = 'cue.legacyMigrated';

interface LegacyMessage {
  role?: string;
  content?: string;
}

interface LegacyChat {
  id?: number | string;
  title?: string;
  createdAt?: string;
  mode?: string;
  messages?: LegacyMessage[];
  makeme?: string;
  code?: string;
  code_language?: string;
}

interface LegacySettings {
  aiProvider?: string;
  aiModel?: string;
  apiKey?: string;
  systemPrompt?: string;
}

export interface LegacyImport {
  cards: Card[];
  provider: ProviderId | null;
  model: string | null;
  apiKey: string | null;
  systemPrompt: string | null;
}

function toTimestamp(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const t = Date.parse(value);
  return Number.isNaN(t) ? fallback : t;
}

/** Convert a chat from the original Cue (plain role/content messages) into a card. Its MAKEME text becomes the spec. */
export function convertLegacyChat(legacy: LegacyChat, now = Date.now()): Card {
  const createdAt = toTimestamp(legacy.createdAt, now);
  const messages: CardMessage[] = [];
  for (const m of legacy.messages ?? []) {
    if (m.role !== 'user' && m.role !== 'assistant') continue;
    const text = typeof m.content === 'string' ? m.content : '';
    messages.push({
      id: newId(),
      role: m.role,
      parts: [{ type: 'text', text }],
      createdAt,
    });
  }
  return {
    id: legacy.id !== undefined ? `legacy-${legacy.id}` : newId(),
    title: legacy.title?.trim() || 'Imported card',
    createdAt,
    updatedAt: createdAt,
    messages,
    code: legacy.code ?? '',
    language: normalizeLanguage(legacy.code_language),
    spec: legacy.makeme ?? '',
    // Nobody has checked the old MAKEME text against the code, so it starts unchecked rather than in step.
    specSync: null,
    review: null,
  };
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Read everything the original Cue left behind. Returns null when there is nothing to migrate or it already ran. */
export function readLegacyData(): LegacyImport | null {
  try {
    if (window.localStorage.getItem(LEGACY_FLAG) === '1') return null;
  } catch {
    return null;
  }
  const legacyChats = readJson<LegacyChat[]>(LEGACY_CHATS);
  const legacySettings = readJson<LegacySettings>(LEGACY_SETTINGS);
  if (!legacyChats && !legacySettings) return null;

  const cards = Array.isArray(legacyChats) ? legacyChats.map((c) => convertLegacyChat(c)) : [];
  const provider =
    legacySettings?.aiProvider === 'openrouter' ? 'openrouter' : legacySettings?.aiProvider === 'openai' ? 'openai' : null;
  return {
    cards,
    provider,
    model: legacySettings?.aiModel ?? null,
    apiKey: legacySettings?.apiKey ?? null,
    systemPrompt: legacySettings?.systemPrompt ?? null,
  };
}

/** Mark the migration as done. The legacy keys are left in place so the old site keeps working if it is ever reopened. */
export function markLegacyMigrated(): void {
  try {
    window.localStorage.setItem(LEGACY_FLAG, '1');
  } catch {
    // storage unavailable; nothing to do
  }
}
